import { Wrench } from "lucide-react";
import ServicePage from "./ServicePage";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../components/ui/carousel";
import { useServicePhotos } from "../hooks/useServicePhotos";

const basePhotos = [
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/jbd70svw_7d1af8a2-a6b1-4a70-86d4-5faf3f33749f.jpeg", alt: "Houten overkapping met pergola - Timmerwerk Breukelen" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/5mg034ry_61c7164a-e3f0-41bd-b959-98927f5eb8e9.jpeg", alt: "Dakconstructie hout in aanbouw - Cardoso Bouw" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/y59rovtn_555a74e4-61c2-499c-80ca-694ddf44f650.jpeg", alt: "Houten constructie met zaagmachine - Timmerwerk project" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/4n5u60q0_6a573654-d445-47cb-9f1d-dccc33def286.jpeg", alt: "Veranda plafond met lichtkoepels - Overkapping op maat" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/0mqom5i5_ff4bf38e-207d-4edd-b920-5f61b18bd263.jpeg", alt: "Afgewerkte ruimte met LED plafond en marmeren vloer" },
];

const faqs = [
  {
    q: "Welke houtsoorten gebruiken jullie?",
    a: "Dat hangt af van de toepassing: Douglas of lariks voor overkappingen en pergola's, hardhout of meranti voor kozijnen, en eiken of MDF voor maatwerk meubels en kasten. Wij adviseren altijd de houtsoort die het beste past bij gebruik, uitstraling en budget.",
  },
  {
    q: "Maken jullie ook meubels en kasten op maat?",
    a: "Ja. Inbouwkasten, boekenkasten, tv-meubels, wandmeubels en andere maatwerk meubels maken wij precies passend voor uw ruimte, inclusief afwerking in de gewenste kleur of houtlook.",
  },
  {
    q: "Kunnen jullie een overkapping of veranda bouwen?",
    a: "Ja, wij ontwerpen en bouwen houten overkappingen, pergola's en veranda's op maat, inclusief fundering, dakbedekking, lichtkoepels en eventueel verlichting.",
  },
  {
    q: "Vervangen jullie ook kozijnen?",
    a: "Ja, wij vervangen oude kozijnen door nieuwe houten of kunststof kozijnen die voldoen aan de huidige isolatienormen, inclusief glas, afwerking en schilderwerk.",
  },
  {
    q: "Heb ik een vergunning nodig voor een overkapping of dakkapel?",
    a: "Dat hangt af van de afmetingen, de plaats op het perceel en de gemeentelijke regels. Veel overkappingen aan de achterzijde zijn vergunningsvrij. Wij adviseren u hierover en helpen waar nodig met de aanvraag.",
  },
  {
    q: "Hoe lang duurt timmerwerk gemiddeld?",
    a: "Kleine klussen zoals het plaatsen van een deur zijn vaak binnen een dag klaar. Een overkapping of maatwerk interieur neemt doorgaans 1 tot 3 weken in beslag, inclusief voorbereiding in de werkplaats.",
  },
];

function TimmerwerkCarousel() {
  const photos = useServicePhotos("timmerwerk", basePhotos);
  
  return (
    <div className="px-12" data-testid="timmerwerk-carousel">
      <Carousel opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {photos.map((photo, i) => (
            <CarouselItem key={i}>
              <div className="overflow-hidden">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
        <CarouselNext className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
      </Carousel>
    </div>
  );
}

export default function TimmerwerkPage() {
  return (
    <ServicePage
      seoTitle="Timmerwerk Breukelen | Kozijnen, Deuren & Maatwerk - Cardoso"
      seoDescription="Professioneel timmerwerk in Breukelen. Kozijnen, deuren, maatwerk meubels, dakkapellen en renovatie timmerwerk. Ervaren timmerlieden. Vraag offerte aan."
      seoKeywords="timmerwerk Breukelen, timmerman Breukelen, kozijnen vervangen, deuren op maat, maatwerk meubels, overkapping op maat, dakkapel, timmerman Utrecht, timmerwerk Hilversum, timmerman Loosdrecht"
      title="Timmerwerk"
      subtitle="Timmerwerk Breukelen"
      heroImage="https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/jbd70svw_7d1af8a2-a6b1-4a70-86d4-5faf3f33749f.jpeg"
      icon={Wrench}
      description="Professioneel timmerwerk op maat in Breukelen en omgeving. Van kozijnen en deuren tot complete maatwerk interieurs. Onze ervaren timmerlieden hebben jarenlange ervaring in zowel nieuwbouw als renovatieprojecten en werken met oog voor detail en vakmanschap."
      features={[
        "Kozijnen plaatsen en vervangen",
        "Deuren op maat gemaakt",
        "Maatwerk kasten en meubels",
        "Renovatie timmerwerk",
        "Overkappingen en pergola's",
        "Plafonds en wanden",
        "Trappen en balustrades",
        "Houten vloeren leggen",
      ]}
      faqs={faqs}
      extraContent={
        <div className="space-y-8">
          {/* PROJECT CAROUSEL */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-6">
              Onze timmerwerk projecten
            </h2>
            <TimmerwerkCarousel />
          </div>

          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A]">
            Timmerwerk op maat voor elke situatie
          </h2>
          <p className="text-slate-600 text-base leading-relaxed">
            Hout is een tijdloos materiaal dat in elk interieur past. Of het nu gaat om 
            klassiek eikenhout of modern MDF, onze ervaren timmerlieden maken op maat 
            wat u nodig heeft. Van het vervangen van oude kozijnen en het plaatsen van 
            nieuwe deuren tot het ontwerpen en bouwen van een complete inbouwkast of 
            boekenrek, wij brengen uw ideeën tot leven.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Kozijnen en ramen
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Verouderde kozijnen zijn niet alleen minder mooi, maar kunnen ook problemen 
            geven met isolatie en tocht. Wij plaatsen nieuwe houten of kunststof kozijnen 
            die voldoen aan de nieuwste isolatienormen. Bij renovatiewerk zorgen wij 
            ervoor dat de nieuwe kozijnen naadloos aansluiten op de bestaande stijl 
            van uw woning.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Overkappingen en pergola's
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Wij bouwen op maat gemaakte houten overkappingen en pergola's die uw tuin 
            verrijken. Van een stijlvolle Douglas hout constructie met zwarte 
            potdekselplanken tot een moderne veranda met lichtkoepels, wij realiseren 
            het geheel naar uw wensen.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Timmerman in uw regio
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Wij voeren timmerwerk uit in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland. 
            Wij komen graag bij u langs voor een vrijblijvende offerte. Wij werken zowel met 
            traditioneel hout als met moderne materialen. Neem contact op en ontdek 
            de mogelijkheden voor uw project.
          </p>
        </div>
      }
    />
  );
}
